import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import styled from "styled-components";
import { Dropdown } from "../Dropdown/Dropdown";
import { Input } from "../Input/Input";
import { countryCodes } from "./countryCodes";
import { DropdownStyles } from "../../types/styles";

const Container = styled.div`
  display: flex;
  flex-direction: column;
  gap: 5px;
  font-size: 13px;
`;

const InputContainer = styled.div`
  display: flex;

  & > *:first-child {
    width: 83px;
  }

  & > *:last-child {
    flex-grow: 1;
  }
`;

const StyledLabel = styled.label<{
  styles: DropdownStyles;
}>`
  font-weight: 700;
  color: ${({ styles }) => styles.OverallGeneralTextColour};
`;

const ErrorMessage = styled.div<{
  styles: DropdownStyles;
}>`
  color: ${({ styles }) => styles.DropdownGeneralValidationErrorColour};
`;

export type MobileInputProps = {
  setCountryCode: React.Dispatch<React.SetStateAction<number>>;
  setCountryAlphaCode: React.Dispatch<React.SetStateAction<string>>;
  setMobileNumber: React.Dispatch<React.SetStateAction<number>>;
  selectedAlpha2Code?: string;
  selectedPhoneNumber?: string;
  displayLabel?: string;
  styles: DropdownStyles;
  errorMessage?: string;
  dataId?: string;
};

export const MobileInput: React.FC<MobileInputProps> = ({
  setCountryCode,
  setCountryAlphaCode,
  setMobileNumber,
  selectedAlpha2Code,
  selectedPhoneNumber,
  displayLabel,
  styles,
  errorMessage,
  dataId,
}) => {
  const { t } = useTranslation();

  const countryCodeOptions = countryCodes.map((code) => ({
    label: `${code.name} ${code.mobileCode}`,
    value: code.alpha2Code,
    leftText: code.alpha2Code,
  }));
  const [alpha2Code, setAlpha2Code] = useState(selectedAlpha2Code ?? "GB");
  const [menuWidth, setMenuWidth] = useState("0px");
  const containerRef = useRef<HTMLDivElement>(null);

  const updateMenuWidth = () => {
    if (containerRef.current) {
      setMenuWidth(`${containerRef.current.offsetWidth}px`);
    }
  };

  useEffect(() => {
    updateMenuWidth();
    window.addEventListener("resize", updateMenuWidth);
    return () => {
      window.removeEventListener("resize", updateMenuWidth);
    };
  }, []);

  // The country code option value is initially set to the alpha-2 code
  // because those are unique and can be used as a key in the Dropdown
  // options map. Here it gets converted to the mobile code which is
  // the value we actually want to store.
  const handleCountryCodeSelect = (alpha2Code: string) => {
    setAlpha2Code(alpha2Code);
    const mobileCode = countryCodes.find(
      (code) => code.alpha2Code === alpha2Code
    )?.mobileCode;

    mobileCode && setCountryCode(Number(mobileCode.replace("+", "")));
  };

  return (
    <Container data-id={dataId + "-top-container"}>
      {displayLabel && (
        <StyledLabel styles={styles}>{displayLabel}</StyledLabel>
      )}
      <InputContainer ref={containerRef}>
        <Dropdown
          options={countryCodeOptions}
          onSelect={(alpha2Code) => {
            setCountryAlphaCode(alpha2Code.toString());
            handleCountryCodeSelect(alpha2Code.toString());
          }}
          selected={alpha2Code}
          ariaLabel={t("SelectYourCountryCode")}
          styles={styles}
          menuWidth={menuWidth}
          isErrored={!!errorMessage}
          leftBorderRadiusOnly
          dataId={dataId + "-country-code"}
        />
        <Input
          onChange={(value) =>
            setMobileNumber(Number.parseInt(value.toString()))
          }
          value={selectedPhoneNumber}
          type="number"
          ariaLabel={t("EnterYourData", {
            data: displayLabel?.toLowerCase() ?? t("Number").toLowerCase(),
          })}
          styles={styles}
          isErrored={!!errorMessage}
          rightBorderRadiusOnly
          min={0}
          dataId={dataId}
        />
      </InputContainer>
      {errorMessage && (
        <ErrorMessage styles={styles} data-id={dataId + "-error"}>
          {errorMessage}
        </ErrorMessage>
      )}
    </Container>
  );
};

export default MobileInput;
